import React, { useState } from 'react';
import { QuestionDefinition, LogicRule, LogicCondition, LogicRuleGroup, LogicAction } from '../../types/survey.types';
import { useToast } from '../common/Toast';

interface BulkLogicEditorProps {
  questions: QuestionDefinition[];
  onApply: (updatedQuestions: QuestionDefinition[]) => void;
  onClose: () => void;
}

type BulkLogicPreset =
  | 'sequential'
  | 'skip_if_no'
  | 'show_if_rating_low'
  | 'show_if_rating_high';

const BulkLogicEditor: React.FC<BulkLogicEditorProps> = ({ questions, onApply, onClose }) => {
  const toast = useToast();
  const [selectedPreset, setSelectedPreset] = useState<BulkLogicPreset>('sequential');
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>(questions.map(q => q.id));

  const presets = [
    {
      id: 'sequential' as BulkLogicPreset,
      name: 'Sequential Questions',
      description: 'Show each question only after previous is answered',
      icon: '➡️'
    },
    {
      id: 'skip_if_no' as BulkLogicPreset,
      name: 'Skip If "No"',
      description: 'Hide follow-ups if user answers "No"',
      icon: '🚫'
    },
    {
      id: 'show_if_rating_low' as BulkLogicPreset,
      name: 'Show If Rating Low',
      description: 'Show follow-ups for ratings ≤2',
      icon: '📉'
    },
    {
      id: 'show_if_rating_high' as BulkLogicPreset,
      name: 'Show If Rating High',
      description: 'Show follow-ups for ratings ≥4',
      icon: '📈'
    }
  ];

  const toggleQuestionSelection = (questionId: string) => {
    setSelectedQuestions(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const applyBulkLogic = () => {
    if (selectedQuestions.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    const updatedQuestions = [...questions];
    const selectedIndices = selectedQuestions.map(id =>
      updatedQuestions.findIndex(q => q.id === id)
    ).sort((a, b) => a - b);

    selectedIndices.forEach((index) => {
      if (index <= 0) return;

      const currentQuestion = updatedQuestions[index];
      const previousQuestion = updatedQuestions[index - 1];

      let newRule: LogicRule | null = null;

      switch (selectedPreset) {
        case 'sequential':
          newRule = createSequentialRule(previousQuestion, currentQuestion);
          break;
        case 'skip_if_no':
          if (previousQuestion.type === 'yesno') {
            newRule = createSkipIfNoRule(previousQuestion, currentQuestion);
          }
          break;
        case 'show_if_rating_low':
          if (['rating', 'scale'].includes(previousQuestion.type)) {
            newRule = createRatingRule(previousQuestion, currentQuestion, 'low');
          }
          break;
        case 'show_if_rating_high':
          if (['rating', 'scale'].includes(previousQuestion.type)) {
            newRule = createRatingRule(previousQuestion, currentQuestion, 'high');
          }
          break;
      }

      if (newRule) {
        updatedQuestions[index] = {
          ...currentQuestion,
          logicRules: [...(currentQuestion.logicRules || []), newRule]
        };
      }
    });

    onApply(updatedQuestions);
    toast.success(`Bulk logic applied to ${selectedIndices.length} question(s)!`);
    onClose();
  };

  const createSequentialRule = (prev: QuestionDefinition, current: QuestionDefinition): LogicRule => {
    const condition: LogicCondition = {
      id: `cond_${Date.now()}`,
      questionId: prev.id,
      operator: 'is_not_empty',
      value: ''
    };

    const ruleGroup: LogicRuleGroup = {
      id: `group_${Date.now()}`,
      operator: 'AND',
      conditions: [condition]
    };

    const action: LogicAction = {
      type: 'show',
      targetQuestionId: current.id
    };

    return {
      id: `rule_${Date.now()}`,
      name: `Show after previous answered`,
      enabled: true,
      ruleGroups: [ruleGroup],
      groupOperator: 'AND',
      actions: [action],
      createdAt: new Date(),
      updatedAt: new Date()
    };
  };

  const createSkipIfNoRule = (prev: QuestionDefinition, current: QuestionDefinition): LogicRule => {
    const condition: LogicCondition = {
      id: `cond_${Date.now()}`,
      questionId: prev.id,
      operator: 'equals',
      value: 'yes'
    };

    const ruleGroup: LogicRuleGroup = {
      id: `group_${Date.now()}`,
      operator: 'AND',
      conditions: [condition]
    };

    const action: LogicAction = {
      type: 'show',
      targetQuestionId: current.id
    };

    return {
      id: `rule_${Date.now()}`,
      name: `Show only if previous is Yes`,
      enabled: true,
      ruleGroups: [ruleGroup],
      groupOperator: 'AND',
      actions: [action],
      createdAt: new Date(),
      updatedAt: new Date()
    };
  };

  const createRatingRule = (prev: QuestionDefinition, current: QuestionDefinition, threshold: 'low' | 'high'): LogicRule => {
    const condition: LogicCondition = {
      id: `cond_${Date.now()}`,
      questionId: prev.id,
      operator: threshold === 'low' ? 'less_or_equal' : 'greater_or_equal',
      value: threshold === 'low' ? 2 : 4
    };

    const ruleGroup: LogicRuleGroup = {
      id: `group_${Date.now()}`,
      operator: 'AND',
      conditions: [condition]
    };

    const action: LogicAction = {
      type: 'show',
      targetQuestionId: current.id
    };

    return {
      id: `rule_${Date.now()}`,
      name: `Show if rating ${threshold === 'low' ? '≤2' : '≥4'}`,
      enabled: true,
      ruleGroups: [ruleGroup],
      groupOperator: 'AND',
      actions: [action],
      createdAt: new Date(),
      updatedAt: new Date()
    };
  };

  const selectedPresetInfo = presets.find(p => p.id === selectedPreset);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-lg shadow-xl w-[520px] max-w-[95vw] flex flex-col" style={{ maxHeight: '70vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-lg">⚡</span>
            <h2 className="text-base font-semibold text-gray-900">Bulk Logic</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Preset Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Logic Pattern</label>
            <div className="grid grid-cols-2 gap-2">
              {presets.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => setSelectedPreset(preset.id)}
                  className={`p-2.5 rounded-lg border text-left transition-all flex items-start gap-2 ${
                    selectedPreset === preset.id
                      ? 'border-[var(--color-accent)] bg-[var(--color-surface)] ring-1 ring-[var(--color-accent)]'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-lg flex-shrink-0">{preset.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-xs text-gray-900">{preset.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{preset.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Question Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Select Questions ({selectedQuestions.length})</label>
              <button
                onClick={() => setSelectedQuestions(selectedQuestions.length === questions.length ? [] : questions.map(q => q.id))}
                className="text-xs font-medium"
                style={{ color: 'var(--color-accent)' }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                {selectedQuestions.length === questions.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg max-h-[200px] overflow-y-auto">
              {questions.map((question, index) => (
                <label
                  key={question.id}
                  className={`flex items-start gap-3 p-2.5 cursor-pointer transition-colors ${
                    index !== questions.length - 1 ? 'border-b border-gray-100' : ''
                  } ${
                    selectedQuestions.includes(question.id)
                      ? 'bg-[var(--color-surface)]'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedQuestions.includes(question.id)}
                    onChange={() => toggleQuestionSelection(question.id)}
                    className="mt-0.5 h-4 w-4 border-gray-300 rounded"
                    style={{
                      accentColor: 'var(--color-accent)'
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-900 truncate">
                      Q{index + 1}. {question.title}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {question.type} • {question.logicRules?.length || 0} rule(s)
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Info */}
          {selectedPresetInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex gap-2">
                <span className="text-sm">ℹ️</span>
                <div className="flex-1">
                  <p className="text-xs text-blue-900 font-medium">How it works</p>
                  <p className="text-xs text-blue-800 mt-1">{selectedPresetInfo.description}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={applyBulkLogic}
            disabled={selectedQuestions.length === 0}
            className="px-4 py-2 text-sm rounded-md font-medium transition-colors"
            style={{
              backgroundColor: selectedQuestions.length === 0 ? '#D1D5DB' : 'var(--color-accent)',
              color: selectedQuestions.length === 0 ? '#6B7280' : 'white',
              cursor: selectedQuestions.length === 0 ? 'not-allowed' : 'pointer',
              opacity: selectedQuestions.length === 0 ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (selectedQuestions.length > 0) {
                e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedQuestions.length > 0) {
                e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              }
            }}
          >
            Apply to {selectedQuestions.length} Question{selectedQuestions.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkLogicEditor;
